/* ----------------------------------------------------
              Imports
----------------------------------------------------- */
import * as ActiveStorage from "@rails/activestorage";
import Rails from "@rails/ujs";
import "trix";
import "@rails/actiontext";
import * as Turbo from "@hotwired/turbo-rails";
import posthog from "posthog-js";

// Start Rails UJS and ActiveStorage
Rails.start();
ActiveStorage.start();


/* ----------------------------------------------------
              Stimulus
----------------------------------------------------- */
import "./controllers/index";

/* ----------------------------------------------------
              Vendor
----------------------------------------------------- */
import "./vendor/chartjs";
import "./vendor/choices";
import "./vendor/filestack";
import "./vendor/active_storage";
import "./vendor/stripe";
import "./vendor/tippy";
import "./vendor/trix";

/* ----------------------------------------------------
              Components
----------------------------------------------------- */
import "./components/checkbox";
import "./components/confetti";
import "./components/field";
import "./components/form";
import "./components/google_maps_manager";
import "./components/mapbox_manager";
import "./components/input";
import "./components/modal";
import "./components/nav";
import "./components/radios";
import "./components/select";
import "./components/sticky";
import "./components/tabs";

/* ----------------------------------------------------
              Pages
----------------------------------------------------- */
import "./pages/calendar/index";
import "./pages/directories/show";
import "./pages/guides/show";
import "./pages/heartbeat/index";
import "./pages/home/index";
import "./pages/job_announcements/new";
import "./pages/job_changes/job_change";
import "./pages/job_changes/update";
import "./pages/job_files/view/show";
import "./pages/job_transactions/shared/new";
import "./pages/jobs/view/index";
import "./pages/jobs/form";
import "./pages/jobs/view/show_activity";
import "./pages/landing/index";
import "./pages/notes/note";
import "./pages/notes/create";
import "./pages/notes/destroy";
import "./pages/notes/update";

window.posthog = posthog;

let posthogInitialized = false;
let lastIdentifiedUserId = null;
let lastPlanroomId = null;
let lastCapturedPageviewUrl = null;

const posthogBodyConfig = () => {
  const body = document.body;

  return {
    enabled: body?.dataset.posthogEnabled === "true",
    localMode: body?.dataset.posthogLocalMode === "true",
    apiKey: body?.dataset.posthogApiKey,
    apiHost: body?.dataset.posthogApiHost,
    userId: body?.dataset.posthogUserId,
    userEmail: body?.dataset.posthogUserEmail,
    userName: body?.dataset.posthogUserName,
    userTrade: body?.dataset.posthogUserTrade,
    userCompany: body?.dataset.posthogUserCompany,
    planroomId: body?.dataset.posthogPlanroomId,
    planroomName: body?.dataset.posthogPlanroomName
  };
};

const initializePosthog = (config) => {
  if (posthogInitialized || !config.enabled || !config.apiKey) return;

  posthog.init(config.apiKey, {
    api_host: config.apiHost,
    defaults: "2026-01-30",
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: true,
    advanced_disable_flags: config.localMode,
    advanced_disable_feature_flags: config.localMode,
    disable_surveys: config.localMode,
    disable_conversations: config.localMode,
    disable_product_tours: config.localMode,
    request_batching: !config.localMode,
    disable_compression: config.localMode,
    debug: config.localMode,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask]"
    }
  });

  posthogInitialized = true;
};

const syncPosthogUser = (config) => {
  if (!posthogInitialized) return;

  if (!config.userId) {
    if (lastIdentifiedUserId) {
      posthog.reset();
      posthogInitialized = false;
      lastIdentifiedUserId = null;
      lastPlanroomId = null;
      lastCapturedPageviewUrl = null;
      initializePosthog(config);
    }

    return;
  }

  const personProperties = {
    email: config.userEmail,
    name: config.userName,
    trade: config.userTrade,
    company: config.userCompany
  };

  if (lastIdentifiedUserId === config.userId) {
    posthog.setPersonProperties(personProperties);
    return;
  }

  posthog.identify(String(config.userId), personProperties);
  lastIdentifiedUserId = config.userId;
};

const syncPosthogPlanroom = (config) => {
  if (!posthogInitialized) return;

  if (!config.planroomId) {
    lastPlanroomId = null;
    return;
  }

  if (lastPlanroomId === config.planroomId) return;

  posthog.group("planroom", String(config.planroomId), {
    name: config.planroomName
  });
  lastPlanroomId = config.planroomId;
};

const captureTurboPageview = () => {
  if (!posthogInitialized) return;

  const currentUrl = window.location.href;
  if (lastCapturedPageviewUrl === currentUrl) return;

  posthog.capture("$pageview", {
    $current_url: currentUrl,
    path: window.location.pathname,
    search: window.location.search
  });
  lastCapturedPageviewUrl = currentUrl;
};

document.addEventListener("turbo:load", () => {
  const config = posthogBodyConfig();

  initializePosthog(config);
  syncPosthogUser(config);
  syncPosthogPlanroom(config);
  captureTurboPageview();
});
